import React, { useState, useMemo } from 'react';
import { ShoppingCart, IndianRupee, BarChart3, TrendingUp, Award } from 'lucide-react';

// Components
import { StatCard } from './CommonComponents';

// Helpers
import { formatCurrency } from '../Helpers';


const Reports = ({ sales, inventory, shopName = 'StoreFlow' }) => {
    const [period, setPeriod] = useState('daily'); // 'daily', 'weekly', 'monthly'

    const reportData = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let startDate;

        switch (period) {
            case 'weekly':
                const dayOfWeek = now.getDay();
                startDate = new Date(today);
                const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as start of week
                startDate.setDate(today.getDate() - diff);
                break;
            case 'monthly':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                break;
            case 'daily':
            default:
                startDate = today;
                break;
        }

        const filteredSales = sales.filter(sale => sale.saleDate.toDate() >= startDate);

        let totalRevenue = 0;
        let totalCost = 0;
        const itemSales = {};

        filteredSales.forEach(sale => {
            totalRevenue += sale.totalAmount;
            sale.items.forEach(item => {
                totalCost += (item.purchasePrice || 0) * item.quantity;

                if (!itemSales[item.itemName]) {
                    itemSales[item.itemName] = { quantity: 0, revenue: 0 };
                }
                itemSales[item.itemName].quantity += item.quantity;
                itemSales[item.itemName].revenue += item.sellingPrice * item.quantity;
            });
        });

        const totalProfit = totalRevenue - totalCost;
        const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
        const topSellingItems = Object.entries(itemSales)
            .map(([name, data]) => ({ name, ...data }))
            .sort((a, b) => b.quantity - a.quantity)
            .slice(0, 10);

        return {
            totalRevenue,
            totalProfit,
            numberOfSales: filteredSales.length,
            topSellingItems,
            profitMargin,
        };
    }, [sales, period]);

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl p-4 sm:p-8 text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center space-x-4">
                        <div className="bg-white bg-opacity-20 rounded-full p-3 sm:p-4">
                            <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold">{shopName} - Reports</h1>
                            <p className="text-orange-100 mt-1 text-sm sm:text-base">Analyze your sales performance and trends</p>
                        </div>
                    </div>

                    {/* Period Selector */}
                    <div className="bg-white bg-opacity-20 rounded-lg p-1 w-full sm:w-auto">
                        <div className="grid grid-cols-3 sm:flex">
                            {['daily', 'weekly', 'monthly'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => setPeriod(p)}
                                    className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${period === p
                                        ? 'bg-white text-orange-600 shadow-sm'
                                        : 'text-white hover:bg-white hover:bg-opacity-20'
                                        }`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <StatCard title="Revenue" value={formatCurrency(reportData.totalRevenue)} icon={IndianRupee} color="green" />
                <StatCard title="Profit" value={formatCurrency(reportData.totalProfit)} icon={TrendingUp} color="blue" />
                <StatCard title="Profit Margin" value={`${reportData.profitMargin.toFixed(2)}%`} icon={BarChart3} color="purple" />
                <StatCard title="Number of Sales" value={reportData.numberOfSales} icon={ShoppingCart} color="yellow" />
            </div>

            {/* Top Selling Products */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <Award className="h-5 w-5 mr-2 text-orange-600" />
                        Top Selling Products ({period})
                    </h2>
                </div>
                <div className="p-6">
                    {reportData.topSellingItems.length > 0 ? (
                        <>
                            {/* Desktop Table View */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="p-4 font-medium text-gray-600">Rank</th>
                                            <th className="p-4 font-medium text-gray-600">Product Name</th>
                                            <th className="p-4 font-medium text-gray-600 text-right">Units Sold</th>
                                            <th className="p-4 font-medium text-gray-600 text-right">Total Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.topSellingItems.map((item, index) => (
                                            <tr key={item.name} className="border-b hover:bg-gray-50 transition-colors">
                                                <td className="p-4">
                                                    <div className="flex items-center">
                                                        {index < 3 ? (
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-yellow-500' :
                                                                index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                                                                }`}>
                                                                {index + 1}
                                                            </div>
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-sm">
                                                                {index + 1}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="bg-orange-100 rounded-lg p-2">
                                                            <ShoppingCart className="h-4 w-4 text-orange-600" />
                                                        </div>
                                                        <p className="font-semibold text-gray-900">{item.name}</p>
                                                    </div>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {item.quantity} units
                                                    </span>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <p className="font-bold text-green-600">{formatCurrency(item.revenue)}</p>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="lg:hidden space-y-3">
                                {reportData.topSellingItems.map((item, index) => (
                                    <div key={item.name} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                {index < 3 ? (
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-yellow-500' :
                                                        index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                                                        }`}>
                                                        {index + 1}
                                                    </div>
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-sm">
                                                        {index + 1}
                                                    </div>
                                                )}
                                                <div className="bg-orange-100 rounded-lg p-2">
                                                    <ShoppingCart className="h-4 w-4 text-orange-600" />
                                                </div>
                                                <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p className="text-gray-500 mb-1">Units Sold</p>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {item.quantity} units
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-gray-500 mb-1">Total Revenue</p>
                                                <p className="font-bold text-green-600">{formatCurrency(item.revenue)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No sales data available</h3>
                            <p className="text-gray-500">Sales data for the selected period will appear here</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Reports;